import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { leaguesApi } from '@/services/api';
import { USE_MOCK_DATA } from '@/lib/constants';

// API contract types (envelope already unwrapped by ApiClient)
export type LeagueRole = 'owner' | 'admin' | 'member';

export interface LeagueListItem {
  id: string;
  name: string;
  description?: string | null;
  sport?: 'ncaa' | 'wc' | string;
  memberCount: number;
  maxMembers?: number | null;
  isOwner: boolean;
  isMember: boolean;
  privacy?: string;
  yourRank?: number | null;
  topPlayer?: {
    userId: string;
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
    score?: number | null;
  } | null;
  settings?: {
    scoringType?: string;
    allowLateEntry?: boolean;
    publicLeaderboard?: boolean;
  };
  prizePool?: number | null;
  prizeNotes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface LeagueMemberRow {
  userId: string;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  role: LeagueRole;
  joinedAt?: string;
  score?: number | null;
  correctPicks?: number | null;
  totalPicks?: number | null;
  streak?: number | null;
  rank?: number | null;
}

export interface LeagueDetail {
  league: LeagueListItem & {
    inviteCode?: string | null; // only for owner/admin per contract
    role: LeagueRole; // current user role
  };
  members: LeagueMemberRow[];
}

interface LeaguesState {
  leagues: LeagueListItem[];
  publicLeagues: LeagueListItem[];
  current: LeagueDetail | null;
  isLoading: boolean;
  error: string | null;

  fetchLeagues: () => Promise<void>;
  fetchPublicLeagues: () => Promise<void>;
  fetchLeague: (id: string) => Promise<void>;
  createLeague: (data: { name: string; description?: string; maxMembers?: number; privacy?: string; sport?: string }) => Promise<string>;
  joinLeague: (id: string, inviteCode?: string) => Promise<void>;
  joinLeagueByCode: (inviteCode: string) => Promise<void>;
  leaveLeague: (id: string) => Promise<void>;
  clearCurrent: () => void;
}

// Minimal mocks as fallback while backend settles (and for local dev)
const mockLeagues: LeagueListItem[] = [
  {
    id: 'mock-league-1',
    name: 'Office Warriors',
    description: 'Company bracket challenge',
    memberCount: 24,
    maxMembers: 50,
    isOwner: true,
    isMember: true,
    yourRank: 5,
    createdAt: new Date().toISOString(),
  },
];

export const useLeaguesStore = create<LeaguesState>()(
  devtools(
    (set, get) => ({
      leagues: [],
      publicLeagues: [],
      current: null,
      isLoading: false,
      error: null,

      fetchLeagues: async () => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            set({ leagues: mockLeagues, isLoading: false });
            return;
          }
          const res = await leaguesApi.list();
          // expected: { leagues: LeagueListItem[] }
          set({ leagues: res.leagues ?? [], isLoading: false });
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to load leagues', isLoading: false });
        }
      },

      fetchPublicLeagues: async () => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            set({ publicLeagues: [], isLoading: false });
            return;
          }
          const res = await leaguesApi.discover();
          set({ publicLeagues: res.leagues ?? [], isLoading: false });
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to load public leagues', isLoading: false });
        }
      },

      fetchLeague: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            const league = mockLeagues.find((l) => l.id === id) ?? mockLeagues[0];
            set({
              current: {
                league: { ...(league as any), role: 'owner', inviteCode: 'MOCK123' },
                members: [],
              },
              isLoading: false,
            });
            return;
          }
          const res = await leaguesApi.detail(id);
          // expected: { league, members }
          set({ current: res, isLoading: false });
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to load league', isLoading: false });
        }
      },

      createLeague: async (data) => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            const id = `mock-${Date.now()}`;
            set({
              leagues: [
                ...get().leagues,
                {
                  id,
                  name: data.name,
                  description: data.description,
                  memberCount: 1,
                  maxMembers: data.maxMembers,
                  isOwner: true,
                  isMember: true,
                  createdAt: new Date().toISOString(),
                },
              ],
              isLoading: false,
            });
            return id;
          }
          const res = await leaguesApi.create(data);
          // Backend returns: { league: { id, ... }, message }
          const leagueId = res.league?.id ?? res.id;
          await get().fetchLeagues();
          set({ isLoading: false });
          return leagueId;
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to create league', isLoading: false });
          throw e;
        }
      },

      joinLeague: async (id: string, inviteCode?: string) => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            set({ isLoading: false });
            return;
          }
          await leaguesApi.join(id, { inviteCode });
          await get().fetchLeagues();
          set({ isLoading: false });
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to join league', isLoading: false });
          throw e;
        }
      },

      joinLeagueByCode: async (inviteCode: string) => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            set({ isLoading: false });
            return;
          }
          await leaguesApi.joinByCode({ inviteCode });
          await get().fetchLeagues();
          set({ isLoading: false });
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to join league', isLoading: false });
          throw e;
        }
      },

      leaveLeague: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            set({ leagues: get().leagues.filter((l) => l.id !== id), isLoading: false });
            return;
          }
          await leaguesApi.leave(id);
          await get().fetchLeagues();
          set({ current: null, isLoading: false });
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to leave league', isLoading: false });
          throw e;
        }
      },

      clearCurrent: () => set({ current: null }),
    }),
    { name: 'leagues-store' }
  )
);
